import { Box, Button, Checkbox, CircularProgress, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, List, ListItem, ListItemText, Step, StepConnector, StepLabel, Stepper, styled, Tooltip, Typography } from '@mui/material';
import '../DataPipeline/dataPipeline.scss';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ReactComponent as UploadIcon } from '../../assets/cloud-upload.svg';
import { ReactComponent as DownloadIcon } from '../../assets/dp-download.svg';
import { ReactComponent as InfoIcon } from '../../assets/dp-info.svg';
import { ReactComponent as TickIcon } from '../../assets/tick-icon-dp.svg';
import { ReactComponent as ImportIcon } from '../../assets/dp-upload-icon.svg';
import { ReactComponent as WarningIcon } from '../../assets/warning-dp-job.svg';
import { ReactComponent as SuccessTicketIcon } from '../../assets/icon-vector.svg';
import { useDropzone } from "react-dropzone";
import TeradataIcon from '../../assets/icon-teradata.svg';
import DDHIcon from '../../assets/icon-ddh.svg';
import { useState, useCallback } from 'react';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { Column } from 'react-data-grid';
import { bulkJobAdditionDp } from 'service/service';
import RowTypography from 'components/Table/RowTypography';
import { getIsV2 } from 'utils/versionToggle';

interface Row {
  [key: string]: any;
  Job: string,
  Platform: string,
  Scheduler: string,
  isValid: boolean,
  silverTableName: string,
  dataAssetName: string,
  pipelineName: string,
  adfName: string,
  triggerName: string
}

const staticKeys = new Set(['goldTableNames','task_Name','taskName', 'JobName', 'selected', 'id', 'JobId', 'ProgramId', 'ProjectId', 'Cyclic', 'SLATime', 'SLAODate', 'ADFName', 'Job', 'isValid', 'triggerName', 'jobId', 'platformId', 'cyclic', 'scheduleInfo', 'jobStatus', 'jobName', 'InstanceName', 'Platform', 'Scheduler', 'folderName', 'startTime', 'days', 'schedulerType', 'jobIds', 'adfName', 'dataAssetName', 'silverTableName', 'triggerNames', 'size', 'page', 'filter', 'projectId', 'platformName', 'PipelineName', 'DataAssetName', 'SilverTableName', 'TriggerName', 'workspaceId']);

function toTitleLabel(key: string): string {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return spaced
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getColumns(dynamicKeys: string[], isV2Adf: boolean): Array<Column<Row>> {
  const jobColumnName = isV2Adf ? 'Pipelines' : 'Jobs';
  const columns: Array<Column<Row>> =
    [
      {
        key: 'Job',
        name: jobColumnName,
        cellClass: (row) => (row.isValid ? "" : "invalid-cell-dp-bulk"),
        formatter({ row }) {
          return (

            <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
              <RowTypography>{row.Job ? row.Job : '--'}</RowTypography>
              {!row.isValid && <WarningIcon />}
            </Box>
          );
        },
        width: 335
      },
      {
        key: 'Platform',
        name: 'Platform',
        formatter({ row }) {
          const getPlatformName = (platform: string) => {
            if (platform === 'Teradata') return 'Teradata';
            if (platform === 'BODS') return 'BODS';
            return platform;
          };

          const getPlatformIcon = (platform: string) => {
            if (platform === 'Teradata' || platform === 'BODS') {
              return <img src={TeradataIcon} alt="Teradata" style={{ width: '18px', verticalAlign: 'middle', marginRight: '5px' }} />;
            }
            if (platform === 'DDH') {
              return <img src={DDHIcon} alt="DDH" style={{ width: '18px', verticalAlign: 'middle', marginRight: '5px' }} />;
            }
            return '';
          };

          return (
            <RowTypography>
              {getPlatformIcon(row.Platform)}
              {getPlatformName(row.Platform)}
            </RowTypography>
          );
        },
        width: 335
      },
      {
        key: 'Scheduler',
        name: 'Scheduler',
        formatter({ row }) {
          return (
            <RowTypography>{row.Scheduler ? row.Scheduler : '--'}</RowTypography>
          );
        },
        width: 335
      },

    ];
    columns.push({
      key: 'silverTableName',
      name: 'Silver Table',
      formatter({ row }) {
        return (
          <RowTypography>{row.silverTableName ? row.silverTableName : '--'}</RowTypography>
        );
      },
      width: 335
    },
    {
      key: 'adfName',
      name: 'Instance',
      formatter({ row }) {
        return (
          <RowTypography>{row.adfName ? row.adfName : '--'}</RowTypography>
        );
      },
      width: 335
    },
    {
      key: 'triggerName',
      name: 'Trigger',
      formatter({ row }) {
        return (
          <RowTypography>{row.triggerName ? row.triggerName : '--'}</RowTypography>
        );
      },
      width: 335
    },
    {
      key: 'dataAssetName',
      name: 'Data Asset',
      formatter({ row }) {
        return (
          <RowTypography>{row.dataAssetName ? row.dataAssetName : '--'}</RowTypography>
        );
      },
      width: 335
    },)

  // Add dynamic columns
  for (const key of dynamicKeys) {
    columns.push({
      key,
      name: toTitleLabel(key),
      formatter({ row }) {
        return <RowTypography>{row[key] ? row[key] : '--'}</RowTypography>;
      },
      width: 335
    });
  };

  return columns;
}

interface DragDropFileUploadProps {
  onDropFiles: any;
  onFileUpload: (event: any) => void;
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DragDropFileUpload = ({ onDropFiles, onFileUpload }: DragDropFileUploadProps) => {
  const onDrop = useCallback((acceptedFiles: any) => {
    console.log("Files dropped:", acceptedFiles);
    if (onDropFiles) onDropFiles(acceptedFiles);
  }, [onDropFiles]);

  const { getRootProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"]
    },
    multiple: false,
  });
  return (
    <Box {...getRootProps()} sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <div
        style={{
          width: '100%',
          maxWidth: '680px',
          minHeight: '240px',
          borderRadius: '4px',
          border: '1px dashed rgba(113, 117, 143, 1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          boxSizing: 'border-box',
          backgroundColor: 'rgba(240, 248, 255, 1)',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', maxWidth: '100%' }}>
          <UploadIcon style={{ width: '120px', height: '120px' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Typography style={{ color: 'rgba(113, 117, 143, 1)', whiteSpace: 'nowrap' }}>
              {isDragActive ? 'Drop Here.. ' : 'Drop .csv file or'}
            </Typography>
            <Button component="label" variant="text" tabIndex={-1} sx={{ p: 0, minWidth: 0, lineHeight: 1 }}>
              <Typography style={{ cursor: 'pointer', color: 'rgba(0, 155, 216, 1)', textDecoration: 'underline', fontWeight: 500 }}>
                browse from here
                <VisuallyHiddenInput
                  type="file"
                  accept='.csv'
                  onChange={(event: any) => onFileUpload(event)}
                />
              </Typography>
            </Button>
          </Box>
        </Box>
      </div>
    </Box>
  );
};

const BulletList = ({ points }: { points: string[] }) => {
  return (
    <Box sx={{ minWidth: '50rem', p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        Follow the instruction to import a file
      </Typography>
      <List>
        {points.map((item: string, index: number) => (
          <ListItem key={item} sx={{ pl: 0 }}>
            <ListItemText primary={`• ${item}`} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

const StyledStepIcon = styled('div')(({ active, completed }: { active: boolean; completed?: boolean }) => ({
  width: '34px',
  height: '34px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: active || completed ? '#EFF6FF' : '#71758F',
  color: '#003771',
  fontWeight: 'bold',
  transition: 'all 0.3s ease',
  '& svg path': {
    fill: '#003771',
  },
}));

interface StepIconProps {
  active: boolean;
  completed: boolean;
  index: number;
}

function StepIconForward({ active, completed, index }: StepIconProps) {
  return completed ? (
    <StyledStepIcon active={active} completed>
      <TickIcon />
    </StyledStepIcon>
  ) : (
    <StyledStepIcon active={active}>{index + 1}</StyledStepIcon>
  );
}

function getStepIconComponent(index: number, activeStep: number) {
  return function StepIcon(props: any) {
    return (
      <StepIconForward
        active={props.active}
        completed={activeStep > index}
        index={index}
      />
    );
  };
}

function getDynamicKeys(jobData: any[], staticKeys: Set<string>): string[] {
  if (jobData.length === 0) {
    return [];
  }

  const firstItemKeys = Object.keys(jobData[0]);

  return firstItemKeys.filter(key => !staticKeys.has(key));
}

function buildBulkUploadErrorMessage(
  activeStep: number,
  jobValidity: { inValid: number; valid: number; exist?: number },
  apiMessage: string
): string {
  if(apiMessage) return apiMessage;
  if (activeStep === 0) {
    return 'Sorry, we are unable to read the file. Please use the sample file format and try again.';
  }
  
  const { inValid: invalidCount = 0, valid: validCount = 0, exist: existCount = 0 } = jobValidity;
  // If nothing is invalid or existing, no error message needed
  if (!invalidCount && !existCount) {
    return '';
  }
  
  const parts = [];
  
  if (invalidCount > 0) {
    const invalidLabel = invalidCount === 1 ? 'job is' : 'jobs are';
    parts.push(`${invalidCount} ${invalidLabel} invalid`);
  }
  
  if (existCount > 0) {
    const existLabel = existCount === 1 ? 'job is' : 'jobs are';
    parts.push(`${existCount} ${existLabel} already part of this project`);
  }
  
  let message = parts.join('. ');
  
  if (validCount > 0) {
    const validLabel = validCount === 1 ? 'job' : 'jobs';
    message += `. Only ${validCount} ${validLabel} can be added to the project.`;
  }
  
  return message;
}

function resolveBulkUploadStepContent(
  activeStep: number,
  isFetching: boolean,
  views: {
    stepZeroView: JSX.Element;
    stepOneView: JSX.Element;
    stepTwoView: JSX.Element;
  }
): JSX.Element {
  if (isFetching) {
    return <></>;
  }

  switch (activeStep) {
    case 0:
      return views.stepZeroView;
    case 1:
      return views.stepOneView;
    case 2:
      return views.stepTwoView;
    default:
      return <></>;
  }
}

export const JobBulkUpload = ({ setBulkUploadModal, setSelectedRows, addSchedulesToProject, addProjectLoading, successMsg, successCount, getAddedScheduleList, platform, projectId }: any) => {
  
  const isAdf = platform === 'ADF';
  let buttonText = isAdf ? 'ADD PIPELINES TO PROJECT' : 'ADD JOBS TO PROJECT '
  let infoText = isAdf ? 'Pipelines' : 'Jobs';

  const [loading, setLoading] = useState({
    downloadSampleFile: false,
    fetchData: false
  })
  const [activeStep, setActiveStep] = useState(0)
  const [jobValidity, setJobValidity] = useState({
    valid: 0,
    inValid: 0,
    exist: 0
  })
  const [isJobValid, setIsJobValid] = useState(true);
  const [isJobAdded, setIsJobAdded] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [jobData, setJobData] = useState<any>([])
  const [validJobData, setValidJobData] = useState<any>([])
  const [inValidJobData, setInValidJobData] = useState<any>([])
  const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
  const [apiMessage, setApiMessage] = useState<string>('');
  const filterInfo: any[] = [];
  const filterOptions = {};

  const steps = ['Upload File', 'Confirm & Add'];

  const points = [
    `Metadata for those ${infoText} that are missing can be updated in an excel, or csv file and then uploaded all together.`,
    "Ensure that you always input accurate data.",
    "Incorrect entries may not be accurately recorded.",
  ];

  const StyledLabel = styled(Typography)(({ active, completed }: { active: boolean; completed?: boolean }) => {
    let colorValue;
    if (active || completed) {
      colorValue = '#EFF6FF';
    } else {
      colorValue = '#71758F';
    }
    return {
      fontSize: '1rem',
      fontWeight: 'bold',
      color: colorValue,
      transition: 'all 0.3s ease',
    };
  });

  const StyledConnector = styled(StepConnector)(({ completed }: { completed: boolean }) => ({
    '&.MuiStepConnector-root': {
      top: 22,
      width: '50px',
      flex: 'none',
    },
    '& .MuiStepConnector-line': {
      borderColor: completed ? '#EFF6FF' : '#71758F',
      borderTopWidth: 2,
      transition: 'all 0.3s ease',
    },
  }));

  const handleClose = () => {
    setBulkUploadModal(false)
    isJobAdded && getAddedScheduleList()
  }


  const convertToCSV = (data: any) => {
    const headers = Object.keys(data[0]).join(","); // Extract headers
    const rows = data.map((row: any) => Object.values(row).join(",")); // Extract rows
    return [headers, ...rows].join("\n"); // Combine headers and rows
  };

  // Function to handle the download
  const handleIicsSampleFileDownload = () => {
    const csvData = convertToCSV([
      { Platform: 'IICS', Scheduler: 'IICS', Job: 'Job 1' },
      { Platform: 'IICS', Scheduler: 'IICS', Job: 'Job 2' }
    ]);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csvData], { type: "text/csv" }));
    link.download = 'sample_iics_v2.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleSamplFileDownload = () => {
    let isV2 = getIsV2()
    let data, fileName;

    if (isV2 && platform == 'ADF') {
      // V2 sample columns and data
      data = [
        {
          Platform: 'ADF',
          Scheduler: 'ADF',
          'Instance Name': 'ADF Instance Name',
          'Pipeline Name': 'Pipeline Name',
          'Trigger Name': 'Trigger Name',
          'Data Asset Name': 'Data AssetName',
          'Silver Table Name': 'Silver Table Name'
        },
        {
          Platform: 'ADF',
          Scheduler: 'ADF',
          'Instance Name': 'ADF Instance Name 2',
          'Pipeline Name': 'Pipeline Name 2',
          'Trigger Name': 'Trigger Name 2',
          'Data Asset Name': 'Data AssetName 2',
          'Silver Table Name': 'Silver Table Name 2'
        }
      ];
    } else if (isV2 && platform === 'DDH') {
      data = [
        {
          Platform: platform,
          Scheduler: 'NATIVE',
          JobNames: 'Job 1'
        },
        {
          Platform: platform,
          Scheduler: 'CUSTOM',
          JobNames: 'Job 2'
        }
      ];
    } else if (isV2 && platform === 'ADB') {
      data = [
        {
          Platform: platform,
          Scheduler: 'ADB',
          WorkspaceName: 'Workspace 1',
          Job: 'Job 1'
        },
        {
          Platform: platform,
          Scheduler: 'ADB',
          WorkspaceName: 'Workspace 2',
          Job: 'Job 2'
        }
      ];
    } else if (isV2 && platform === 'MDT') {
      data = [
        {
          Platform: platform,
          Scheduler: 'MDT',
          JobNames: 'Job 1'
        },
        {
          Platform: platform,
          Scheduler: 'MDT',
          JobNames: 'Job 2'
        }
      ];
    } else if (
      isV2 && 
      (platform === 'SQL Server Agent (NewsPage)' || 
        platform === 'SAP BW' || 
        platform === 'BODS')
      ) {
      let schedulers: string[] = [];
      if(platform === 'SAP BW') {
        schedulers = ['SAP BW','HWA'];
      } else if (platform === 'BODS') {
        schedulers = ['Control-M-PGT', 'HWA'];
      } else {
        schedulers = ['SQL Server'];
      }
      data = [
        {
          Platform: platform,
          Scheduler: schedulers[0],
          Job: 'Job 1'
        },
        {
          Platform: platform,
          Scheduler: schedulers[1] ?? schedulers[0],
          Job: 'Job 2'
        }
      ];
    } else {
      // V1 sample columns and data
      data = [
        { Platform: 'Teradata', Scheduler: "HWA", Job: "Job 1" },
        { Platform: 'Teradata', Scheduler: "Control-M", Job: "Job 2" }
      ];
    }

    fileName = `sample_${platform.toLowerCase()}_v2.csv`;
    // Example data
    const csvData = convertToCSV(data); // Convert data to CSV format
    const blob = new Blob([csvData], { type: "text/csv" }); // Create a Blob
    const url = URL.createObjectURL(blob); // Create a URL for the Blob

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName; // Set the file name
    document.body.appendChild(link);
    link.click();
    link.remove(); // Clean up the temporary link
  }

  let requiredData = []

  const onFileUpload = async (event: any) => {
    try {
      setApiMessage('');
      if (!event?.target?.files[0] && !event[0]) {
        setIsJobValid(false);
        return;
      }

      const formData = new FormData();
      setLoading({
        ...loading,
        fetchData: true
      })

      formData.append('file', event?.target?.files[0] || event[0]);
      formData.append('platform', (platform || '').toUpperCase());
      if (projectId) {
        formData.append('projectId', String(projectId));
      }

      let response = await bulkJobAdditionDp(formData);
      const backendMessage = response?.data?.message || '';
      const resData = Array.isArray(response?.data) ? response.data : [];
      const backendRows = Array.isArray(response?.data?.result) ? response.data.result : resData;
      
      // Calculate counts from isValid and isExist flags
      const invalidCount = backendRows.filter((job: any) => !job.isValid && !job.isExist).length;
      const existCount = backendRows.filter((job: any) => job.isExist).length;
      const validCount = backendRows.filter((job: any) => job.isValid).length;

      if (!backendRows.length) {
        setIsJobValid(false);
        setApiMessage(backendMessage || 'No records found in uploaded file.');
        setJobData([]);
        setValidJobData([]);
        setInValidJobData([]);
        setSelectedRows([]);
        setJobValidity({ valid: 0, inValid: 0, exist: 0 });
        setActiveStep(0); // stay on upload step
        return;
      }

      setJobData(backendRows);
      if (response.success) {
        setJobData(response.data)
        let inValidData = response.data.filter((obj: any) => !obj.isValid)
        requiredData = response.data.filter((obj: any) => obj.isValid)

        let jobObjArr: any = []
        requiredData.map((obj: any) => {
          const jobObj: any = {
            jobName: obj.Job,
            jobId: obj.jobId,
            Scheduler: obj.Scheduler,
            cyclic: obj.cyclic,
            Platform: obj.Platform,
            platformId: obj.platformId, 
            scheduleInfo: obj.scheduleInfo
          };
          if (platform === 'ADF') {
            jobObj.adfName = obj.adfName;
            jobObj.dataAssetName = obj.dataAssetName;
            jobObj.silverTableName = obj.silverTableName;
            jobObj.triggerName = obj.triggerName;
          }
          // Add dynamic keys
          for (const key of Object.keys(obj)) {
            if (!staticKeys.has(key) && !(key in jobObj)) {
              jobObj[key] = obj[key];
            }
          };
          jobObjArr.push(jobObj);
        });
        setSelectedRows(jobObjArr)
        setInValidJobData(inValidData)
        setValidJobData(requiredData)
        if (invalidCount > 0 || existCount > 0) {
          setIsJobValid(false)
        } else {
          setIsJobValid(true)
        }
        setJobValidity({
          inValid: invalidCount,
          valid: validCount,
          exist: existCount
        })
      }
      setLoading({
        ...loading,
        fetchData: false
      })

      setActiveStep(1);
    }  catch (err: any) {
      setIsJobValid(false);
      setJobData([]);
      setValidJobData([]);
      setInValidJobData([]);
      setSelectedRows([]);
      setJobValidity({ valid: 0, inValid: 0, exist: 0 });
      setActiveStep(0);

      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong while uploading the file.';
      setApiMessage(msg);
    } finally {
      // ✅ ALWAYS stop spinner
      setLoading((prev: any) => ({
        ...prev,
        fetchData: false
      }));
    }
  };

  const handleValidCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsChecked(checked);
    const updatedData = checked
      ? validJobData
      : [...validJobData, ...inValidJobData];

    setJobData(updatedData);
  };

  const dynamicKeys = getDynamicKeys(jobData, staticKeys);

  const columns = ['Pipelines', 'Instance', 'Pipeline', 'DataAsset', 'Silver Table', 'Trigger', 'Platform', 'Scheduler'];
  const defaultColumnNames = [...(isAdf ? columns : []), ...dynamicKeys];

  const handleAddProject = async () => {
    await addSchedulesToProject()
    setIsJobAdded(true)
    setIsJobValid(true)
    setActiveStep(2)
  }

  const handleSorting = (sortingInfo: any) => {
    console.log("SORTTT", sortingInfo)
    let key = sortingInfo['sortBy']
    let data = jobData
    if (sortingInfo.sortDir == 'asc') {
      data.sort((a: any, b: any) => a[key] && b[key] ? a[key].localeCompare(b[key]) : a.Job.localeCompare(b.Job))
    } else {
      data.sort((a: any, b: any) => a[key] && b[key] ? b[key].localeCompare(a[key]) : b.Job.localeCompare(a.Job))
    }
    setJobData(data)
    setSortingInfo(sortingInfo)
  }

  // STEP 0 VIEW
  const stepZeroView = (
    <Box>
      {!isJobValid && (
        <Box sx={{ px: '14px', pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'rgba(255, 162, 0, 0.15)', borderRadius: '4px', color: 'rgba(255, 162, 0, 1)' }}>
            <WarningIcon style={{ marginRight: '10px' }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
              {buildBulkUploadErrorMessage(activeStep, jobValidity, apiMessage)}
            </Typography>
          </Box>
        </Box>
      )}
      <Box sx={{ px: '14px', pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Typography sx={{ fontWeight: 600, color: '#009BD8', fontSize: '18px' }}>
          Upload a File to Import
        </Typography>
        <Tooltip
          title={<BulletList points={points} />}
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                maxWidth: '50rem',
              },
            },
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <InfoOutlinedIcon style={{ width: '24px', height: '24px', opacity: 0.6, color: '#71758F' }} />
          </span>
        </Tooltip>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#009BD8' }} />
      </Box>

      <Box sx={{ px: '14px', pb: 2, display: 'flex', justifyContent: 'center' }}>
        <DragDropFileUpload onDropFiles={onFileUpload} onFileUpload={onFileUpload} />
      </Box>

      <Box sx={{ px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
        <Button
          startIcon={<DownloadIcon />}
          variant="outlined"
          size="small"
          onClick={platform === 'IICS' ? handleIicsSampleFileDownload : handleSamplFileDownload}
          sx={{
            fontSize: '16px',
            color: 'rgba(0, 155, 216, 1)',
            borderColor: 'rgba(0, 155, 216, 1)',
            backgroundColor: 'white',
            textTransform: 'none',
            px: 3,
            py: 1,
            borderRadius: '4px',
            '&:hover': {
              borderColor: 'rgba(0, 155, 216, 1)',
              backgroundColor: 'rgba(0, 155, 216, 0.05)',
            },
          }}
        >
          Download Sample Files
        </Button>
      </Box>
    </Box>
  );

  // STEP 1 VIEW
  const stepOneView = (
    <Grid container spacing={2} sx={{ margin: 0, width: '100%' }}>
      {!isJobValid && (
        <Grid item xs={12} sx={{ padding: '0px 20px 0px 20px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, backgroundColor: 'rgba(255, 162, 0, 0.15)', borderRadius: '4px', color: 'rgba(255, 162, 0, 1)' }}>
            <WarningIcon style={{ marginRight: '10px' }} />
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
              {buildBulkUploadErrorMessage(activeStep, jobValidity, apiMessage)}
            </Typography>
          </Box>
        </Grid>
      )}

      <Grid item xs={12} sx={{ padding: (!isJobValid ? '20px 20px 0px 20px' : '30px 20px 0px 20px'), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Grid item xs={9}>
          <Box sx={{ display: 'flex', alignItems: 'center', height: '22px', marginBottom: '5px' }}>
            <Typography sx={{ color: 'rgba(15, 55, 100, 1)', display: 'flex', alignItems: 'center', fontSize: '18px', fontWeight: '600' }}>
              Records Review
            </Typography>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: 'rgba(0, 155, 216, 1)', marginLeft: '16px' }} />
          </Box>
        </Grid>
        <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <FormControlLabel
            sx={{
              marginRight: 0,
              '& .MuiFormControlLabel-label': { color: 'rgba(15, 55, 100, 1) !important', fontWeight: 500, marginLeft: '6px' },
              '& .MuiCheckbox-root': { color: 'rgba(15, 55, 100, 1) !important', padding: '3px' },
              '& .MuiCheckbox-root.Mui-disabled': { color: 'rgba(15, 55, 100, 1) !important', opacity: 1 },
            }}
            control={
              <Checkbox
                checked={isChecked}
                onChange={(e: any) => handleValidCheck(e)}
                icon={
                  <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '3px',
                    border: '2px solid rgba(15, 55, 100, 1)',
                    backgroundColor: 'transparent',
                  }} />
                }
                checkedIcon={
                  <Box sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '3px',
                    backgroundColor: 'rgba(15, 55, 100, 1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    <TickIcon style={{ width: 10, height: 10, color: 'white' }} />
                  </Box>
                }
                sx={{
                  padding: 0,
                  '&.Mui-disabled': { opacity: 1 },
                }}
              />
            }
            label="Show valid records only"
          />
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ padding: '10px 20px 0px 20px' }}>
        <Box sx={{
          '& .rdg': {
            '&::-webkit-scrollbar': {
              width: '12px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0, 155, 216, 1)',
              borderRadius: '8px',
              border: '3px solid transparent',
              backgroundClip: 'content-box',
            },
            scrollbarColor: 'rgba(0,155,216,1) transparent',
            '& .bulk-upload-invalid-row': {
              backgroundColor: 'rgba(255, 247, 214, 0.9) !important',
            },
            '& .bulk-upload-invalid-row .rdg-cell': {
              borderLeft: '4px solid rgba(255, 193, 7, 0.9)',
            },
            '& .invalid-cell-dp-bulk': {
              backgroundColor: 'rgba(255, 247, 214, 0.9) !important',
            },
          }
        }}>
          <CustomizedDataGrid
            tableColumns={getColumns(dynamicKeys, isAdf)}
            tableData={jobData}
            tableType="job-bulk-upload"
            defaultColumnNames={defaultColumnNames}
            totalElements={5}
            onSorting={handleSorting}
            currSorting={sortingInfo}
            filterDataOptions={filterOptions}
            currFilter={filterInfo}
            isRenderPagination={false}
            isDateRangeFilter={false}
            isDisabled={false}
            style={{ height: '30vh', width: '100%', minWidth: '100%' }}
          />
        </Box>
      </Grid>
      {
        jobData.length > 0 && <Grid item xs={12} sx={{ padding: '0px 20px 0px 20px' }}>
          <Typography sx={{ color: 'rgba(113, 117, 143, 1)', fontStyle: 'italic', fontSize: '14px', fontWeight: 500 }}>Showing 1-{jobData.length} of {jobData.length} results</Typography>
        </Grid>
      }
      <Grid item xs={12} sx={{ display: 'flex', alignItems: "center", justifyContent: "space-between", padding: '0px 20px 20px 20px' }}>
        <Grid>
          <Button
            component="label"
            variant="pepwiseSecondaryOutline2"
            sx={{
              width: '200px',
              color: 'rgba(0, 155, 216, 1)',
              borderColor: 'rgba(0, 155, 216, 1)',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: 'rgba(0, 155, 216, 1)',
                backgroundColor: 'rgba(0, 155, 216, 0.05)',
              },
            }}
            tabIndex={-1}>
            <ImportIcon style={{ marginRight: '10px', marginBottom: '3px' }} /> Re-upload File <VisuallyHiddenInput
              type="file"
              accept='.csv, .xlsx'
              onChange={onFileUpload}/>
          </Button>
        </Grid>
        <Grid>
          <Button
            variant="text"
            onClick={handleClose}
            sx={{
              color: 'rgba(0, 155, 216, 1)',
              mr: 1,
              textTransform: 'none',
              width: '100px',
            }}
          >
            CANCEL
          </Button>
          <Button
            variant="outlined"
            onClick={handleAddProject}
            disabled={!validJobData.length}
            sx={{
              width: '270px',
              textTransform: 'none',
              color: '#39ACE7',
              borderColor: '#39ACE7',
              backgroundColor: 'white',
              "&.Mui-disabled": {
                color: '#94A3B8 !important',
                borderColor: '#94A3B8 !important',
                opacity: '1 !important',
                WebkitTextFillColor: '#94A3B8 !important',
              },
              '&:hover': {
                borderColor: '#39ACE7',
                backgroundColor: 'rgba(57, 172, 231, 0.05)',
              },
            }}
          >
            {buttonText}
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );

  const successLabel = isAdf ? 'Pipelines' : 'Jobs';
  const successLabelSingle = isAdf ? 'Pipeline' : 'Job';

  // STEP 2 VIEW
  const stepTwoView = addProjectLoading ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "50vh",
      }}
    >
      <CircularProgress
        size={80}
        thickness={4}
        sx={{
          color: "rgba(0, 155, 216, 1)",
        }}/>
      <Typography style={{ color: '#78818c', width: "20rem", textAlign: 'center', fontWeight: '600', fontSize: '24px', marginTop: '30px' }}>Loading...</Typography>
    </Box>
  ) : (
    <Box sx={{ p: 4, textAlign: 'center' }}>

      {successMsg === `${infoText} were already added!` ? (
        <Typography sx={{ textAlign: 'center', mb: 2, color: 'rgba(15, 55, 100, 1)', fontSize: '18px', fontWeight: '500' }}>{successMsg}</Typography>
      ) : (
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={1.5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ width: '65px', height: '65px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><SuccessTicketIcon /></Box>
            </Grid>
            <Grid item xs={10.5}>
              <Box sx={{ textAlign: 'left', mt: 1, fontSize: '28px', fontWeight: '500', color: 'rgba(15, 55, 100, 1)' }}>
                 {successCount} {successCount > 1 ? successLabel : successLabelSingle} added successfully in the Project.</Box>
              <Box sx={{ textAlign: 'left', mt: 1, fontSize: '18px', fontWeight: '400', color: 'rgba(113, 117, 143, 1)' }}>Would you like to add more?</Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {(successMsg == `${infoText} were already added!` || successMsg == 'Record added successfully') && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              color: 'rgba(0, 155, 216, 1)',
              borderColor: 'rgba(0, 155, 216, 1)',
              textTransform: 'none',
              backgroundColor: 'white',
              '&:hover': {
                borderColor: 'rgba(0, 155, 216, 1)',
                backgroundColor: 'rgba(0, 155, 216, 0.05)',
              },
            }}
          >
            SAVE & EXIT
          </Button>
          <Button
            onClick={() => setActiveStep(0)}
            variant="contained"
            sx={{
              backgroundColor: 'rgba(0, 155, 216, 1)',
              color: 'white',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'rgba(0, 135, 190, 1)',
              },
            }}
          >
            ADD MORE
          </Button>
        </Box>
      )
      }
    </Box>
  );

  const content = resolveBulkUploadStepContent(activeStep, loading.fetchData, {
    stepZeroView,
    stepOneView,
    stepTwoView
  });

  return (
    <>
      <DialogTitle bgcolor="primary.main" sx={{ p: '20px 30px !important' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography sx={{ fontWeight: 600, color: 'white', fontSize: '24px', whiteSpace: 'nowrap' }}>
            Bulk Upload
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flex={1}>
            <Stepper activeStep={activeStep} style={{ width: '500px', justifyContent: 'end', padding: '0px', marginLeft: 'auto' }} connector={<StyledConnector completed={activeStep > 0} />}>
              {steps.map((label, index) => {
                const isActive = index === activeStep;
                return (
                  <Step key={label}>
                    <StepLabel StepIconComponent={getStepIconComponent(index, activeStep)}> 
                      <StyledLabel active={isActive} completed={activeStep > index}>{label}</StyledLabel>
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <IconButton aria-label="Close dialog" onClick={handleClose} sx={{ p: 0 }}>
              <CloseIcon fontSize="medium" sx={{ color: '#ffffff' }} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, px: 2, backgroundColor: 'rgba(245, 248, 252, 1)' }}>
        {
          loading.fetchData ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress
                size={80}
                thickness={4}
                sx={{
                  color: "rgba(0, 155, 216, 1)",
                }}
              />
              <Typography style={{ color: '#78818c', width: "20rem", textAlign: 'center', fontWeight: '600', fontSize: '20px', marginTop: '30px' }}>
                Capturing the results.
                <br />
                Please wait for a while...
              </Typography>
            </Box>
          ) : <>{content}</>
        }
      </DialogContent>
    </>
  );
}